import { type RefObject, useCallback, useLayoutEffect, useState } from 'react';
import type * as THREE from 'three';
import type { HexTileCoordinates } from '@/game/hexmap';
import { type Command, isCity } from '@/game/sim';
import type { Toast } from '@/game/view/commandFeedback';
import { tickRoster } from '@/game/view/tickCache';
import { BuildingPanel } from '../BuildingPanel';
import { BuildingsListPanel, type RosterTab, RosterTabs } from '../BuildingsListPanel';
import { CityPanel } from '../CityPanel';
import { EndScreen } from '../EndScreen';
import { EnemyTip } from '../EnemyTip';
import { FocusQueuePanel } from '../FocusQueuePanel';
import { Hud } from '../Hud';
import { EmpireDrawer } from '../hud/EmpireDrawer';
import { GroupsBar } from '../hud/GroupsBar';
import { type HudMode, isHudMode } from '../hud/hudMode';
import { IdleWorkersButton } from '../hud/IdleWorkersButton';
import { KeyLegend } from '../hud/KeyLegend';
import { TopBar } from '../hud/TopBar';
import { Minimap } from '../Minimap';
import { NotificationsPanel } from '../NotificationsPanel';
import { PerfOverlay } from '../PerfOverlay';
import { usePersisted } from '../persisted';
import type { useQuality } from '../QualitySettings';
import { SendTroopsPanel } from '../SendTroopsPanel';
import { CursorTileTip, HoveredTileInfo } from '../TileInfoPanel';
import { UnitsPanel } from '../UnitsPanel';
import { UpgradesDock, UpgradesPill } from '../UpgradesDock';
import { UpgradingPill } from '../UpgradingPill';
import { NO_HIGHLIGHT, type UiStore, useUi } from '../ui/store';
import { WorkersPanel } from '../WorkersPanel';
import { TILE_THICKNESS } from './MatchScene';
import { useSimFrame } from './simFrame';
import type { SpendFlash } from './useCommandFeedback';
import type { MatchModes } from './useMatchModes';
import type { Selection } from './useSelection';
import type { SendTroops } from './useSendTroops';

/**
 * Everything over the 3D view, in the layout picked in ⚙ (hud/hudMode.ts,
 * mockup 06 A or B): the top (TopBar: clock, resources, cities, upgrades,
 * score, players, ⚙ settings), the left column (the Empire drawer above, the
 * minimap bottom left with the tile card over it; contextual: the hovered
 * tile follows the cursor instead), the alert stack top right, the key
 * legend, the selection dock with the city, building and Send troops panels
 * sharing it, and the end screen.
 */
export function GameHud({
  ui,
  dispatch,
  toasts,
  spends,
  cancelled,
  modes,
  selection,
  sendTroops,
  quality,
  mapRadius,
  isHost,
  viewCameraRef,
  panTo,
  panToWorld,
  onSelectCity,
  onShowCity,
}: {
  ui: UiStore;
  dispatch: (command: Command) => void;
  toasts: readonly Toast[];
  /** What landed clicks just spent, flashed under the resources pill. */
  spends: readonly SpendFlash[];
  /** Sites the player cancelled (their removal isn't a loss). */
  cancelled: ReadonlySet<string>;
  modes: MatchModes;
  selection: Selection;
  sendTroops: SendTroops;
  quality: ReturnType<typeof useQuality>;
  mapRadius: number;
  isHost: boolean;
  viewCameraRef: RefObject<THREE.Camera | null>;
  panTo: (pos: HexTileCoordinates) => void;
  panToWorld: (x: number, z: number) => void;
  /** A Cities chip: select the city (while placing, place from it). */
  onSelectCity: (id: string) => void;
  /** Select a city and go there (idle-worker notifications). */
  onShowCity: (id: string) => void;
}) {
  const frame = useSimFrame();
  const { view: state, playerId, rules, terrain, cityNumbers } = frame;
  // Subscribed, so a rename shows in these panels at once, not on the next tick.
  const names = useUi(ui, (st) => st.names);
  const { placementMode, targetMode } = modes;
  const { selectedUnitIds, selectedBuildingId } = selection;
  const selfEliminated = state.players[playerId]?.eliminated ?? false;
  const selectedBuilding = selectedBuildingId ? state.buildings[selectedBuildingId] : undefined;
  const selectedIsCity = !!selectedBuilding && isCity(selectedBuilding, rules);
  const closeBuilding = () => ui.set({ selectedBuildingId: null });
  // Hovering an upgrade card points at the units it affects (map rings, Units list).
  const highlightIds = useUi(ui, (s) => s.highlightIds);
  const highlight = useCallback(
    (ids: readonly string[]) => ui.set({ highlightIds: ids.length > 0 ? ids : NO_HIGHLIGHT }),
    [ui]
  );

  const upgradesOpen = useUi(ui, (s) => s.upgradesOpen);
  const toggleUpgrades = () => ui.set((s) => ({ upgradesOpen: !s.upgradesOpen }));

  // Left column roster: Units or Buildings (persisted, like the city panel's tab).
  const [savedRosterTab, setRosterTab] = usePersisted<string>('rosterTab', 'units');
  const rosterTab: RosterTab =
    savedRosterTab === 'buildings' || savedRosterTab === 'workers' ? savedRosterTab : 'units';
  const [savedMode, setMode] = usePersisted<string>('hudMode', 'edges');
  const mode: HudMode = isHudMode(savedMode) ? savedMode : 'edges';
  // Each layout has its own drawer default: open at the edges, closed in
  // contextual. Set before the first paint, and again on switching.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the match's opening default only
  useLayoutEffect(() => {
    ui.set({ empireOpen: mode === 'edges' });
  }, []);
  const switchMode = (next: HudMode) => {
    setMode(next);
    ui.set({ empireOpen: next === 'edges' });
  };
  // Where NotificationsPanel portals the minimap (bottom left) and the alert stack (top right).
  const [minimapSlot, setMinimapSlot] = useState<HTMLDivElement | null>(null);
  const [stackSlot, setStackSlot] = useState<HTMLDivElement | null>(null);
  const rosterTabs = (
    <RosterTabs
      tab={rosterTab}
      onTab={setRosterTab}
      units={Object.values(state.units).filter((u) => u.owner === playerId).length}
      buildings={Object.values(state.buildings).filter((b) => b.owner === playerId).length}
      workers={tickRoster(state, { terrain, rules, playerId }).count}
    />
  );

  return (
    <>
      <TopBar
        ui={ui}
        dispatch={dispatch}
        quality={quality}
        spends={spends}
        placementMode={placementMode}
        placementCityId={modes.placementCityId}
        selectedBuildingId={selectedBuildingId}
        onSelectCity={onSelectCity}
        onPanToCity={selection.panToCity}
        mode={mode}
        onMode={switchMode}
        upgrades={
          !selfEliminated && (
            <>
              <UpgradesPill open={upgradesOpen} onToggle={toggleUpgrades} />
              <UpgradingPill
                dispatch={dispatch}
                onLocate={(id) => selection.selectListedBuilding(id, true)}
              />
            </>
          )
        }
      />
      <Hud
        dispatch={dispatch}
        selectedUnitIds={selectedUnitIds}
        ui={ui}
        toasts={toasts}
        placementMode={placementMode}
        placementCityId={modes.placementCityId}
        placementError={modes.placementError}
        onCancelPlacement={modes.cancelPlacement}
        targeting={modes.targetBanner}
        onCancelTargeting={modes.cancelTargeting}
        onFoundAt={(unitId) => modes.enterTargeting({ kind: 'found', unitId })}
        onAttackMove={() => modes.enterTargeting({ kind: 'attackMove' })}
      />
      {selectedBuilding &&
        selectedIsCity &&
        !upgradesOpen &&
        placementMode === null &&
        !selfEliminated && (
          <CityPanel
            state={state}
            realState={frame.real}
            city={selectedBuilding}
            number={cityNumbers.get(selectedBuilding.id) ?? 0}
            cityNumbers={cityNumbers}
            names={names}
            onHighlight={highlight}
            playerId={playerId}
            rules={rules}
            terrain={terrain}
            dispatch={dispatch}
            onPlace={modes.enterPlacement}
            onSelectUnits={(ids) => selection.selectMany(ids, false)}
            selectedUnitIds={selection.selectedUnitIds}
            onPan={() => panTo(selectedBuilding.pos)}
            onClose={closeBuilding}
          />
        )}
      {selectedBuilding &&
        !selectedIsCity &&
        placementMode === null &&
        targetMode === null &&
        !upgradesOpen &&
        !selfEliminated && (
          <BuildingPanel
            state={state}
            building={selectedBuilding}
            playerId={playerId}
            rules={rules}
            terrain={terrain}
            dispatch={dispatch}
            cityNumbers={cityNumbers}
            names={names}
            onHighlight={highlight}
            onClose={closeBuilding}
            onSetWatch={(buildingId) => modes.enterTargeting({ kind: 'watch', buildingId })}
          />
        )}
      {sendTroops.target &&
        placementMode === null &&
        !selectedBuilding &&
        selectedUnitIds.length === 0 &&
        !selfEliminated && (
          <SendTroopsPanel
            key={`${sendTroops.target.id ?? ''}@${sendTroops.target.pos.q},${sendTroops.target.pos.r}`}
            state={state}
            playerId={playerId}
            rules={rules}
            cityNumbers={cityNumbers}
            names={names}
            target={sendTroops.target}
            onSend={sendTroops.send}
            onFocusTarget={() => sendTroops.targetPos && panTo(sendTroops.targetPos)}
            onClose={sendTroops.close}
          />
        )}
      {/* After the other docks, so it sits over them (same slot). */}
      {upgradesOpen && !selfEliminated && (
        <UpgradesDock
          dispatch={dispatch}
          onHighlight={highlight}
          onClose={() => ui.set({ upgradesOpen: false, highlightIds: NO_HIGHLIGHT })}
        />
      )}
      {/* Left HUD column: the Empire drawer (idle workers, then the roster,
          which takes the free height and scrolls), then the minimap bottom
          left (portalled in by NotificationsPanel, which owns the alert
          pings) with the hovered tile's card over its top edge, so hovering
          never resizes the roster. */}
      <div className="pointer-events-none absolute bottom-3 left-3 top-[3.75rem] z-10 flex w-60 flex-col justify-between gap-2">
        <EmpireDrawer ui={ui}>
          <IdleWorkersButton onCycle={selection.cycleIdle} />
          {/* Units | Buildings | Workers share one slot (tabs). */}
          <div className="flex min-h-12 flex-1 flex-col">
            {rosterTab === 'workers' ? (
              <WorkersPanel
                selectedUnitIds={selectedUnitIds}
                onSelectUnit={selection.selectUnit}
                onSelectMany={selection.selectMany}
                dispatch={dispatch}
                header={rosterTabs}
              />
            ) : rosterTab === 'buildings' ? (
              <BuildingsListPanel
                selectedBuildingId={selectedBuildingId}
                onSelectBuilding={selection.selectListedBuilding}
                header={rosterTabs}
              />
            ) : (
              <UnitsPanel
                ui={ui}
                selectedUnitIds={selectedUnitIds}
                highlightIds={highlightIds}
                onHighlight={highlight}
                onSelectUnit={selection.selectUnit}
                onFocusUnit={selection.focusUnit}
                onSelectGroup={selection.toggleUnits}
                onTapGroup={selection.tapGroup}
                onSelectMany={selection.selectMany}
                dispatch={dispatch}
                header={rosterTabs}
              />
            )}
          </div>
        </EmpireDrawer>
        <div className="relative shrink-0">
          <div className="absolute bottom-full left-0 mb-2 flex max-h-[45vh] w-60 flex-col justify-end">
            <HoveredTileInfo ui={ui} inspectKey={selection.inspectKey} hover={mode === 'edges'} />
          </div>
          <div ref={setMinimapSlot} className="flex flex-col empty:hidden" />
        </div>
      </div>
      {mode === 'contextual' && <CursorTileTip ui={ui} />}
      <KeyLegend ui={ui} />
      {/* Top right, under the bar: the alert stack (portalled in by NotificationsPanel). */}
      <div
        ref={setStackSlot}
        className="pointer-events-none absolute right-3 top-[3.75rem] z-10 flex max-h-[calc(100vh-4.5rem)] w-64 flex-col"
      />
      <NotificationsPanel
        onFocus={panTo}
        onSelectCity={onShowCity}
        onSelectUnits={(ids) => selection.selectMany(ids, false)}
        onSendTroops={selfEliminated ? undefined : sendTroops.open}
        onOpenUpgrades={selfEliminated ? undefined : () => ui.set({ upgradesOpen: true })}
        cancelled={cancelled}
        cameraRef={viewCameraRef}
        minimapSlot={minimapSlot}
        stackSlot={stackSlot}
        minimap={(pings) => (
          <Minimap
            mapRadius={mapRadius}
            pings={pings}
            cameraRef={viewCameraRef}
            groundY={TILE_THICKNESS}
            onPan={panToWorld}
          />
        )}
      />
      {!selfEliminated && (
        <GroupsBar ui={ui} selectedUnitIds={selectedUnitIds} onDigit={selection.tapDigit} />
      )}
      {!selfEliminated && <FocusQueuePanel selectedUnitIds={selectedUnitIds} dispatch={dispatch} />}
      <EnemyTip ui={ui} />
      <PerfOverlay isHost={isHost} />
      <EndScreen />
    </>
  );
}
